import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Users,
  Award,
  Calendar,
  Target,
  Shield,
  Heart,
  BookOpen,
  Eye,
  CheckCircle,
} from "lucide-react";

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleImageError = (imageSrc) => {
    setImageLoadErrors((prev) => new Set([...prev, imageSrc]));
  };

  const getImageSrc = (src) => {
    return imageLoadErrors.has(src)
      ? `https://via.placeholder.com/400x300/2563eb/ffffff?text=RACOHI+Community`
      : src;
  };

const slides = [
  {
    image: "images/cover.jpg",
    title: "Healthy Communities",
    subtitle: "To reduce Morbidity and Mortality of Chronic diseases including Communicable (Hepatitis B & C, HIV, TB) and Non-Communicable diseases (Cancers, Diabetes, Hypertension).",
  },
  {
    image:
      "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
    title: "Sustainable Impact",
    subtitle: "To provide Equitable, Integrated and Sustainable health and social services to Vulnerable populations in Rakai District through Advocacy, Partnerships & Community driven Interventions.",
  },
];

  const stats = [
    { number: "2,000+", label: "Girls & Women Targeted", icon: Users },
    { number: "130+", label: "Healthcare Providers Trained", icon: Award },
    { number: "18", label: "Months Implementation", icon: Calendar },
    { number: "4", label: "Key Program Areas", icon: Target },
  ];

  const features = [
    {
      icon: Shield,
      title: "Prevention Programs",
      description:
        "Enhance community awareness and shift social norms to prevent SGBV against AGYW through comprehensive education campaigns.",
      highlights: [
        "Community dialogues",
        "Awareness campaigns",
        "Male engagement",
      ],
    },
    {
      icon: Heart,
      title: "Protection Services",
      description:
        "Strengthen support systems and referral pathways for AGYW survivors through safe spaces and comprehensive care.",
      highlights: ["Safe spaces", "Counseling services", "Legal aid support"],
    },
    {
      icon: Users,
      title: "Empowerment Initiatives",
      description:
        "Equip AGYW with knowledge, skills, and resources to assert their rights and make informed decisions about their lives.",
      highlights: [
        "Life skills training",
        "Vocational training",
        "Mentorship programs",
      ],
    },
    {
      icon: BookOpen,
      title: "Community Coordination",
      description:
        "Foster multi-sectoral collaboration among stakeholders to ensure a cohesive and effective SGBV response system.",
      highlights: [
        "Stakeholder meetings",
        "Policy advocacy",
        "Resource coordination",
      ],
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <div className="homepage">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Hero Section */}
      <section className="hero-section">
        {/* Background Slides */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`hero-slide ${index === currentSlide ? "active" : ""}`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}

        {/* Dark Overlay */}
        <div className="hero-overlay"></div>

        {/* Content */}
        <div className="container position-relative hero-content">
          <div className="row align-items-center min-vh-100">
            <div className="col-lg-7">
              <div className="hero-text">
                <h1 className="hero-title">{slides[currentSlide].title}</h1>
                <p className="hero-subtitle">{slides[currentSlide].subtitle}</p>
                <div className="hero-buttons">
                  <a
                    href="/contact"
                    className="btn btn-warning btn-lg btn-hero-primary"
                  >
                    Get Involved <ArrowRight className="ms-2" size={20} />
                  </a>
                  <a
                    href="/about"
                    className="btn btn-outline-light btn-lg btn-hero-secondary"
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="hero-images-container">
                <div className="hero-floating-images">
                  <div className="floating-image floating-image-1">
                    <img
                      src={getImageSrc("images/community/comm3.jpg")}
                      alt="Community Engagement"
                      className="hero-float-img"
                      onError={() =>
                        handleImageError("images/community/comm3.jpg")
                      }
                      loading="eager"
                    />
                    <div className="floating-image-overlay">
                      <div className="overlay-badge">
                        <Users size={18} />
                        <span>Community Impact</span>
                      </div>
                    </div>
                  </div>

                  <div className="floating-image floating-image-2">
                    <img
                      src={getImageSrc("images/community/comm2.jpg")}
                      alt="Empowerment Programs"
                      className="hero-float-img"
                      onError={() =>
                        handleImageError("images/community/comm2.jpg")
                      }
                      loading="eager"
                    />
                    <div className="floating-image-overlay">
                      <div className="overlay-badge">
                        <Heart size={18} />
                        <span>Empowerment</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="hero-decorative-elements">
                  <div className="deco-circle deco-circle-1"></div>
                  <div className="deco-circle deco-circle-2"></div>
                  <div className="deco-square"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <button className="hero-nav hero-nav-prev" onClick={prevSlide}>
          <ChevronLeft size={24} />
        </button>
        <button className="hero-nav hero-nav-next" onClick={nextSlide}>
          <ChevronRight size={24} />
        </button>

        {/* Indicators */}
        <div className="hero-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`hero-indicator ${
                index === currentSlide ? "active" : ""
              }`}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="row g-4">
            {stats.map((stat, index) => (
              <div key={index} className="col-lg-3 col-md-6">
                <div
                  className="stat-card"
                  style={{
                    transform: `translateY(${
                      Math.sin((scrollY + index * 100) * 0.005) * 8
                    }px)`,
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className="stat-icon">
                    <stat.icon size={28} />
                  </div>
                  <h3 className="stat-number">{stat.number}</h3>
                  <p className="stat-label">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Approach</span>
            <h2 className="section-title">
              Comprehensive Solutions for Lasting Change
            </h2>
            <p className="section-description">
              Our four-pillar approach ensures holistic support for adolescent
              girls and young women through evidence-based interventions and
              community-centered strategies.
            </p>
          </div>

          <div className="row g-4">
            {features.map((feature, index) => (
              <div key={index} className="col-lg-6">
                <div
                  className="feature-card"
                  style={{
                    transform: `translateY(${
                      Math.sin((scrollY + index * 200) * 0.003) * 12
                    }px)`,
                    animationDelay: `${index * 0.15}s`,
                  }}
                >
                  <div className="feature-icon">
                    <feature.icon size={32} />
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                    <ul className="feature-highlights">
                      {feature.highlights.map((highlight, idx) => (
                        <li key={idx}>
                          <CheckCircle size={14} />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="about-image-container">
                <img
                  src={getImageSrc("images/cover.jpg")}
                  alt="RACOHI Community Work"
                  className="about-image"
                  onError={() => handleImageError("images/cover.jpg")}
                  loading="lazy"
                />
                <div className="about-image-overlay"></div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="about-content">
                <span className="section-badge">About RACOHI</span>
                <h1 className="">
                  Transforming Health Outcomes for Communities & Women
                </h1>
                <p className="hero-description">
                  Rakai Community Health Initiative (RACOHI) is a registered
                  Community-Based Organization dedicated to controlling
                  infectious diseases (HIV, TB & Malaria) while empowering girls
                  and women through integrated health and protection services.
                </p>

                <div className="mission-vision">
                  <div className="mv-item">
                    <div className="mv-icon">
                      <Eye size={20} />
                    </div>
                    <div className="mv-content">
                      <h6>Our Vision</h6>
                      <p>
                        A Rakai Community with controlled HIV, TB and Malaria
                        epidemics and empowered, healthy Girls & Women.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="about-buttons">
                  <button className="btn btn-primary btn-lg">
                    Learn More <ArrowRight className="ms-2" size={18} />
                  </button>
                  <button className="btn btn-outline-primary btn-lg">
                    Our Programs
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        :root {
          --primary-blue: #2563eb;
          --secondary-blue: #1e40af;
          --accent-teal: #06b6d4;
          --accent-orange: #f59e0b;
          --success-green: #10b981;
          --text-gray: #6b7280;
          --light-gray: #f8fafc;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #374151;
        }

        /* Hero Section */
        .hero-section {
          height: 100vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 2s ease-in-out;
        }

        .hero-slide.active {
          opacity: 1;
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            rgba(37, 99, 235, 0.85),
            rgba(6, 182, 212, 0.75)
          );
          z-index: 2;
        }

        .hero-content {
          z-index: 3;
          color: white;
        }

        .hero-title {
          font-size: clamp(3rem, 6vw, 5rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 2rem;
          margin-top: 2rem;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
        }

        .hero-subtitle {
          font-size: clamp(1.2rem, 2.5vw, 1.5rem);
          font-weight: 400;
          margin-bottom: 3rem;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
          line-height: 1.4;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .btn-hero-primary {
          background-color: var(--accent-orange);
          border: none;
          padding: 1rem 2.5rem;
          border-radius: 50px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-hero-primary:hover {
          background-color: #d97706;
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(245, 158, 11, 0.3);
        }

        .btn-hero-secondary {
          padding: 1rem 2.5rem;
          border-radius: 50px;
          border: 2px solid rgba(255, 255, 255, 0.8);
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-hero-secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }

        /* Hero Images Container */
        .hero-images-container {
          position: relative;
          height: 500px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-floating-images {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .floating-image {
          position: absolute;
          border-radius: 2rem;
          overflow: hidden;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
          transition: all 0.4s ease;
          backdrop-filter: blur(10px);
          border: 3px solid rgba(255, 255, 255, 0.2);
        }

        .floating-image-1 {
          top: 20%;
          right: 10%;
          width: 280px;
          height: 350px;
          z-index: 4;
          animation: heroFloat1 8s ease-in-out infinite;
        }

        .floating-image-2 {
          bottom: 10%;
          left: 15%;
          width: 240px;
          height: 300px;
          z-index: 3;
          animation: heroFloat2 8s ease-in-out infinite;
          animation-delay: 4s;
        }

        .hero-float-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: transform 0.4s ease;
          background: linear-gradient(45deg, #f0f0f0, #e0e0e0);
        }

        .floating-image:hover {
          transform: scale(1.05) translateY(-10px);
          box-shadow: 0 40px 120px rgba(0, 0, 0, 0.4);
        }

        .floating-image:hover .hero-float-img {
          transform: scale(1.1);
        }

        .floating-image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(
            to top,
            rgba(37, 99, 235, 0.9),
            transparent
          );
          padding: 1.5rem;
          transform: translateY(100%);
          transition: transform 0.4s ease;
        }

        .floating-image:hover .floating-image-overlay {
          transform: translateY(0);
        }

        .overlay-badge {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: white;
          font-size: 0.95rem;
          font-weight: 600;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .overlay-badge svg {
          color: var(--accent-orange);
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
        }

        /* Hero Decorative Elements */
        .hero-decorative-elements {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }

        .deco-circle {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(
            45deg,
            var(--accent-orange),
            var(--accent-teal)
          );
          opacity: 0.1;
          animation: decorFloat 12s ease-in-out infinite;
        }

        .deco-circle-1 {
          top: 10%;
          left: 5%;
          width: 120px;
          height: 120px;
          animation-delay: 2s;
        }

        .deco-circle-2 {
          bottom: 20%;
          right: 5%;
          width: 80px;
          height: 80px;
          animation-delay: 6s;
        }

        .deco-square {
          position: absolute;
          top: 50%;
          right: 20%;
          width: 60px;
          height: 60px;
          background: linear-gradient(
            45deg,
            var(--success-green),
            var(--primary-blue)
          );
          border-radius: 20%;
          opacity: 0.08;
          animation: decorFloat 10s ease-in-out infinite reverse;
          animation-delay: 4s;
        }

        /* Animations */
        @keyframes heroFloat1 {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(2deg);
          }
        }

        @keyframes heroFloat2 {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(-1deg);
          }
        }

        @keyframes decorFloat {
          0%,
          100% {
            transform: translateY(0px) scale(1);
          }
          50% {
            transform: translateY(-30px) scale(1.1);
          }
        }

        .hero-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 4;
        }

        .hero-nav:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-50%) scale(1.1);
        }

        .hero-nav-prev {
          left: 30px;
        }

        .hero-nav-next {
          right: 30px;
        }

        .hero-indicators {
          position: absolute;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 4;
        }

        .hero-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .hero-indicator.active {
          background: var(--accent-orange);
          transform: scale(1.2);
        }

        /* Stats Section */
        .stats-section {
          padding: 5rem 0;
          background: var(--light-gray);
        }

        .stat-card {
          background: white;
          border-radius: 1rem;
          padding: 2.5rem 1.5rem;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .stat-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .stat-icon {
          background: linear-gradient(
            135deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          width: 70px;
          height: 70px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--primary-blue);
          margin-bottom: 0.5rem;
        }

        .stat-label {
          color: var(--text-gray);
          font-weight: 500;
          margin: 0;
        }

        /* Features Section */
        .features-section {
          padding: 6rem 0;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1rem;
          display: inline-block;
        }

        .section-title {
          font-size: clamp(2.5rem, 4vw, 3.5rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .section-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .feature-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          display: flex;
          gap: 1.5rem;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .feature-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
        }

        .feature-icon {
          background: linear-gradient(
            135deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          width: 70px;
          height: 70px;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .feature-content {
          flex: 1;
        }

        .feature-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .feature-description {
          color: var(--text-gray);
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .feature-highlights {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-highlights li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          color: var(--text-gray);
          font-size: 0.9rem;
        }

        .feature-highlights li svg {
          color: var(--success-green);
        }

        /* About Preview Section */
        .about-preview-section {
          padding: 6rem 0;
          background: var(--light-gray);
        }

        .about-image-container {
          position: relative;
        }

        .about-image {
          width: 100%;
          height: 500px;
          object-fit: cover;
          border-radius: 1.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          background: linear-gradient(45deg, #f0f0f0, #e0e0e0);
        }

        .about-image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            rgba(37, 99, 235, 0.1),
            rgba(6, 182, 212, 0.1)
          );
          border-radius: 1.5rem;
        }

        .about-content {
          padding-left: 3rem;
        }

        .about-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .about-description {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .mission-vision {
          margin-bottom: 2.5rem;
        }

        .mv-item {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .mv-icon {
          background: rgba(37, 99, 235, 0.1);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-blue);
          flex-shrink: 0;
        }

        .mv-content h6 {
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 0.5rem;
        }

        .mv-content p {
          color: var(--text-gray);
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .about-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .about-buttons .btn {
          padding: 0.75rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .about-buttons .btn:hover {
          transform: translateY(-2px);
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive */
        @media (max-width: 992px) {
          .hero-images-container {
            margin-top: 3rem;
            height: 400px;
          }

          .floating-image-1 {
            width: 220px;
            height: 280px;
            right: 5%;
          }

          .floating-image-2 {
            width: 180px;
            height: 240px;
            left: 10%;
          }
        }

        @media (max-width: 768px) {
          .hero-nav {
            display: none;
          }

          .about-content {
            padding-left: 0;
            margin-top: 3rem;
          }

          .feature-card {
            flex-direction: column;
            text-align: center;
          }

          .feature-icon {
            margin: 0 auto;
          }

          .hero-images-container {
            height: 300px;
            margin-top: 2rem;
          }

          .floating-image-1 {
            width: 180px;
            height: 220px;
            top: 10%;
            right: 5%;
          }

          .floating-image-2 {
            width: 160px;
            height: 200px;
            bottom: 10%;
            left: 5%;
          }

          .hero-title {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }
        }

        @media (max-width: 576px) {
          .hero-images-container {
            display: none;
          }

          .hero-buttons {
            flex-direction: column;
            align-items: center;
          }

          .hero-buttons .btn {
            width: 100%;
            max-width: 280px;
          }
        }

        /* Image Loading States */
        .hero-float-img,
        .about-image {
          opacity: 0;
          animation: fadeIn 0.5s ease-in-out forwards;
        }

        .hero-float-img[src*="placeholder"],
        .about-image[src*="placeholder"] {
          font-size: 14px;
          font-weight: 600;
          color: white;
          text-align: center;
          background: var(--primary-blue) !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          .floating-image-1,
          .floating-image-2,
          .deco-circle,
          .deco-square {
            animation: none;
          }

          .stat-card,
          .feature-card {
            animation: none;
          }

          .hero-slide {
            transition: none;
          }
        }

        /* Bootstrap Overrides */
        .btn-primary {
          background-color: var(--primary-blue);
          border-color: var(--primary-blue);
        }

        .btn-primary:hover {
          background-color: var(--secondary-blue);
          border-color: var(--secondary-blue);
        }

        .btn-warning {
          background-color: var(--accent-orange);
          border-color: var(--accent-orange);
          color: white;
        }

        .btn-warning:hover {
          background-color: #d97706;
          border-color: #d97706;
          color: white;
        }

        .btn-outline-light:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.8);
          color: white;
        }

        /* Enhanced hover effects */
        .floating-image:hover {
          z-index: 5;
        }

        .hero-indicator:hover {
          background: rgba(255, 255, 255, 0.8);
          transform: scale(1.1);
        }

        /* Focus states for accessibility */
        .hero-nav:focus,
        .hero-indicator:focus {
          outline: 2px solid var(--accent-orange);
          outline-offset: 2px;
        }

        .btn:focus {
          box-shadow: 0 0 0 0.25rem rgba(37, 99, 235, 0.25);
        }

        .floating-image:focus-within {
          outline: 2px solid var(--accent-orange);
          outline-offset: 2px;
        }

        /* High contrast support */
        @media (prefers-contrast: high) {
          .hero-overlay {
            background: rgba(0, 0, 0, 0.7);
          }

          .floating-image-overlay {
            background: rgba(0, 0, 0, 0.8);
          }

          .section-badge {
            background: var(--primary-blue);
            color: white;
          }

          .floating-image {
            border: 3px solid white;
          }
        }

        /* Print styles */
        @media print {
          .hero-nav,
          .hero-indicators,
          .hero-buttons,
          .about-buttons,
          .hero-floating-images,
          .hero-decorative-elements {
            display: none;
          }

          .hero-section {
            height: auto;
            min-height: 400px;
          }

          .stats-section,
          .features-section,
          .about-preview-section {
            page-break-inside: avoid;
          }
        }

        /* Enhanced visual effects */
        .floating-image::before {
          content: "";
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          background: linear-gradient(
            45deg,
            var(--accent-orange),
            var(--accent-teal)
          );
          border-radius: 2rem;
          z-index: -1;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .floating-image:hover::before {
          opacity: 0.2;
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: var(--light-gray);
        }

        ::-webkit-scrollbar-thumb {
          background: var(--primary-blue);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--secondary-blue);
        }

        /* Enhanced typography */
        .hero-title,
        .section-title,
        .about-title {
          letter-spacing: -0.02em;
        }

        /* Better button interactions */
        .btn:active {
          transform: translateY(0) !important;
        }

        /* Image error handling improvements */
        .hero-float-img[src*="placeholder"]::before,
        .about-image[src*="placeholder"]::before {
          content: "📷";
          font-size: 2rem;
          margin-bottom: 1rem;
        }

        /* Enhanced animations */
        .stat-card,
        .feature-card {
          will-change: transform;
        }

        .floating-image {
          will-change: transform;
        }

        /* Better mobile experience */
        @media (max-width: 480px) {
          .section-title {
            font-size: 2rem;
          }

          .about-title {
            font-size: 1.8rem;
          }

          .hero-text {
            text-align: center;
          }
        }

        /* Enhanced visual depth */
        .stat-card,
        .feature-card {
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        /* Better loading states */
        .hero-float-img:not([src*="placeholder"]),
        .about-image:not([src*="placeholder"]) {
          transition: opacity 0.3s ease-in-out;
        }

        /* Enhanced interactivity */
        .overlay-badge {
          transform: translateY(10px);
          opacity: 0;
          transition: all 0.3s ease;
        }

        .floating-image:hover .overlay-badge {
          transform: translateY(0);
          opacity: 1;
        }

        /* Better spacing on mobile */
        @media (max-width: 768px) {
          .stats-section,
          .features-section,
          .about-preview-section {
            padding: 4rem 0;
          }

          .section-header {
            margin-bottom: 3rem;
          }
        }

        /* Enhanced glass effect */
        .floating-image {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        /* Improved hover states */
        .floating-image:hover .floating-image-overlay {
          background: linear-gradient(
            to top,
            rgba(37, 99, 235, 0.95),
            rgba(6, 182, 212, 0.8)
          );
        }

        /* Enhanced depth and shadows */
        .floating-image-1 {
          filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3));
        }

        .floating-image-2 {
          filter: drop-shadow(0 15px 30px rgba(0, 0, 0, 0.25));
        }
      `}</style>

      {/* Bootstrap JS */}
      <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
    </div>
  );
};

export default HomePage;
